import { useState, useEffect, useRef } from "react";

/**
 * Hook that smoothly animates between real progress milestones so the user
 * sees a continuous 0→100 instead of discrete jumps (e.g. 33% → 67%).
 *
 * - Quickly catches up when the real progress jumps ahead.
 * - Slowly and steadily creeps forward between real updates (~70 s to reach 90%).
 * - Caps simulated progress at 90% so the bar never sprints to near-100% and
 *   then freezes — 90% looks like "still working" rather than "nearly done".
 * - Snaps to 100 once the real progress hits 100.
 */
export function useAnimatedProgress(
  realProgress: number,
  isActive: boolean,
): number {
  const [display, setDisplay] = useState(0);
  const realRef = useRef(realProgress);

  // Keep the ref in sync without restarting the interval
  useEffect(() => {
    realRef.current = realProgress;
  }, [realProgress]);

  useEffect(() => {
    // When the upload finishes / errors, snap to final value
    if (!isActive) {
      setDisplay(realProgress >= 100 ? 100 : 0);
      return;
    }

    // Reset when a new active upload starts
    setDisplay(0);

    const TICK_MS = 80;

    const id = setInterval(() => {
      setDisplay((prev) => {
        const target = realRef.current;

        // Done – snap immediately
        if (target >= 100) return 100;

        // Behind the real value → catch up quickly
        if (prev < target) {
          const diff = target - prev;
          const step = Math.max(0.5, diff * 0.12);
          return Math.min(prev + step, target);
        }

        // Ahead of (or equal to) real value → simulate slow creep.
        // Cap at 90 instead of 98 so the bar doesn't rush to near-100% and
        // then visibly freeze. A slow, steady climb to 90% looks much more
        // natural than a sprint to 98% followed by a stall.
        // The reduced multiplier (0.003 vs 0.015) dramatically lowers the
        // initial speed, giving a gentle, uniform pace across the whole range.
        const cap = 90;
        if (prev >= cap) return prev;

        const remaining = cap - prev;
        const creep = Math.max(0.04, remaining * 0.003);
        return Math.min(prev + creep, cap);
      });
    }, TICK_MS);

    return () => clearInterval(id);
    // Only restart the timer when the active flag flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return Math.min(Math.round(display), 100);
}
